import {
  type AppError,
  InvalidTokenError,
  type Result,
  type ResultWithOauthToken,
} from '@packages/utils';
import {type UseQueryOptions, useQuery} from '@tanstack/react-query';
import {parseCookies} from 'nookies';
import {DEFAULT_API_VERSION} from '../../config/apiVersion';
import {defaultClient} from '../../lib/defaultClient';
import {
  GetLinkNoticeResponseSchema,
  type GetLinkNoticeResponseType,
} from '../entities/linkNotices/schema';
import {sendApiRequest} from '../usecases/auth/sendApiRequest';
import {useIsSessionExpired} from '../usecases/auth/useIsSessionExpired';

type GetLinkNoticesArg = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type ResultGetLinkNotices = ResultWithOauthToken<
  GetLinkNoticeResponseType,
  AppError
>;

type UseGetLinkNoticesArg = {
  config?: Omit<
    UseQueryOptions<
      GetLinkNoticeResponseType,
      AppError,
      GetLinkNoticeResponseType,
      readonly unknown[]
    >,
    'queryKey' | 'queryFn'
  >;
};

export const getLinkNotices = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetLinkNoticesArg): Promise<ResultGetLinkNotices> => {
  const request = async (
    accessToken: string,
  ): Promise<Result<GetLinkNoticeResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    return defaultClient.get(
      `/${version}/link-notices`,
      GetLinkNoticeResponseSchema,
    );
  };

  return sendApiRequest<GetLinkNoticeResponseType>({
    cookies,
    request,
    version,
    authRequired: true,
  });
};

export const useGetLinkNotices = ({config}: UseGetLinkNoticesArg = {}) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useQuery<
    GetLinkNoticeResponseType,
    AppError,
    GetLinkNoticeResponseType,
    readonly unknown[]
  >({
    ...(config ?? ({} as any)),
    queryKey: ['useGetLinkNotices'],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getLinkNotices({cookies});

      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      if (!result.ok) {
        throw new Error(result.error);
      }

      return result.value;
    },
  });
};
